import React, { useState, useEffect, useContext, useMemo } from 'react';
import { AuthContext } from '../../contexts/AuthContext';
import LoadingSpinner from '../../components/LoadingSpinner';
import './Rider.css';

export default function RiderDelivered() {
  const { api, user } = useContext(AuthContext);

  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [refreshing, setRefreshing] = useState(false);

  const riderId = String(user?.id || '');

  useEffect(() => {
    fetchOrders();
  }, []);

  const fetchOrders = async () => {
    try {
      setError('');
      const response = await api.get('/orders');
      const data = Array.isArray(response.data)
        ? response.data
        : (response.data?.orders || response.data?.data || []);
      setOrders(data);
    } catch (err) {
      console.error('Fetch orders error:', err);
      setError('Failed to load orders');
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  };

  const handleRefresh = () => {
    setRefreshing(true);
    fetchOrders();
  };

  const getDeliveredDate = (order) => {
    const d = order?.delivered_at || order?.deliveredAt;
    const dt = d ? new Date(d) : null;
    return dt && !isNaN(dt.getTime()) ? dt : null;
  };

  const getCreatedDate = (order) => {
    const d = order?.created_at || order?.createdAt;
    const dt = d ? new Date(d) : null;
    return dt && !isNaN(dt.getTime()) ? dt : null;
  };

  const deliveredOrders = useMemo(() => {
    const mineDelivered = orders
      .filter((o) => String(o?.rider_id || o?.riderId || '') === riderId)
      .filter((o) => o?.status?.toLowerCase() === 'delivered');

    return mineDelivered.sort((a, b) => {
      const ad = (getDeliveredDate(a) || getCreatedDate(a) || new Date(0)).getTime();
      const bd = (getDeliveredDate(b) || getCreatedDate(b) || new Date(0)).getTime();
      return bd - ad;
    });
  }, [orders, riderId]);

  const totalEarnings = useMemo(() => {
    return deliveredOrders.length * 120;
  }, [deliveredOrders]);

  if (loading) {
    return <LoadingSpinner message="Loading delivered orders..." />;
  }

  return (
    <div className="rider-delivered">
      <div className="container">
        <div className="page-header">
          <div>
            <h1 className="page-title">
              <span className="title-icon">✅</span>
              Delivered Orders
            </h1>
            <p className="page-subtitle">Your completed deliveries</p>
          </div>
          <button
            className="btn btn-outline btn-sm"
            onClick={handleRefresh}
            disabled={refreshing}
          >
            {refreshing ? '🔄 Refreshing...' : '🔄 Refresh'}
          </button>
        </div>

        {error && (
          <div className="alert alert-error">
            <span className="alert-icon">⚠️</span>
            <span className="alert-text">{error}</span>
          </div>
        )}

        {/* Summary */}
        <div className="delivered-summary">
          <div className="summary-item">
            <div className="summary-icon">✅</div>
            <div className="summary-content">
              <div className="summary-value">{deliveredOrders.length}</div>
              <div className="summary-label">Total Deliveries</div>
            </div>
          </div>

          <div className="summary-item">
            <div className="summary-icon">💰</div>
            <div className="summary-content">
              <div className="summary-value">Rs. {totalEarnings}</div>
              <div className="summary-label">Total Earnings</div>
            </div>
          </div>
        </div>

        {/* Delivered Orders List */}
        {deliveredOrders.length === 0 ? (
          <div className="empty-state">
            <div className="empty-state-icon">✅</div>
            <h2 className="empty-state-title">No delivered orders</h2>
            <p className="empty-state-text">
              Your completed deliveries will appear here
            </p>
          </div>
        ) : (
          <div className="delivered-orders-list">
            {deliveredOrders.map((order) => {
              const orderNo = order?.order_number || `#${order?.id}`;
              const address = order?.delivery_address || order?.deliveryAddress || '—';
              const total = order?.total_amount || order?.totalAmount || 0;

              const deliveredAt = getDeliveredDate(order);
              const deliveredText = deliveredAt
                ? deliveredAt.toLocaleString('en-US', {
                    month: 'short',
                    day: 'numeric',
                    hour: '2-digit',
                    minute: '2-digit'
                  })
                : null;

              return (
                <div key={order.id} className="delivered-order-card">
                  <div className="delivered-card-header">
                    <span className="delivered-order-number">{orderNo}</span>
                    <span className="delivered-badge">✅ DELIVERED</span>
                  </div>

                  <div className="delivered-card-body">
                    {deliveredText && (
                      <div className="delivered-info-row">
                        <span className="info-icon">🕒</span>
                        <span className="info-text">{deliveredText}</span>
                      </div>
                    )}

                    <div className="delivered-info-row">
                      <span className="info-icon">📍</span>
                      <span className="info-text">{address}</span>
                    </div>

                    <div className="delivered-card-footer">
                      <div className="delivered-amount-section">
                        <span className="amount-label">Order Amount:</span>
                        <span className="amount-value">Rs. {total}</span>
                      </div>

                      <div className="delivered-earning-section">
                        <span className="earning-label">Your Earning:</span>
                        <span className="earning-value">Rs. 120</span>
                      </div>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
}